import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Activity,
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Download,
  Landmark,
  Save,
  Wallet,
  FileSpreadsheet,
} from "lucide-react";
import { useGetAllBranches } from "../service/branchService";
import { useGetAllCounters } from "../service/counterService";
import {
  useEodPreview,
  useFinalizeEod,
  useGetFinalEodReport,
  downloadEodPreviewPdf,
  downloadFinalEodPdf,
  downloadEodPreviewExcel,
  downloadFinalEodExcel,
} from "../service/eodService";
import {
  EodReportResponse,
  EodCounterCountRequest,
} from "../types/eodTypes";
import ConfirmPopup from "../components/ConfirmPopup";
import { cn } from "../utils/cn";

const fmt = (value: number | null, currency: string | null = "OMR") => {
  if (value === null || value === undefined) return "-";
  
  let decimals = 2;
  if (currency === "OMR" || currency === "BHD" || currency === "KWD") decimals = 3;
  if (currency === "JPY") decimals = 0;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

const today = () => new Date().toISOString().slice(0, 10);

function EodBalance() {
  const [businessDate, setBusinessDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedCounter, setSelectedCounter] = useState("");
  const [counts, setCounts] = useState<Record<number, { countedCash: number | ""; verifiedBank: number | "" }>>({});
  const [reportData, setReportData] = useState<EodReportResponse | null>(null);
  
  const [isConfirmFinalizeOpen, setIsConfirmFinalizeOpen] = useState(false);

  // Fetch Branches
  const { data: branchData } = useGetAllBranches({
    params: { search: "", isActive: true, page: 0, size: 100, sortBy: "branchName", sortDir: "asc" },
  });

  // Fetch Counters for the selected branch
  const { data: counterData } = useGetAllCounters({
    params: {
      branchId: Number(selectedBranch),
      search: "",
      page: 0,
      size: 100,
    }
  });

  // Clear counter and report data when branch changes
  useEffect(() => {
    setSelectedCounter("");
    setReportData(null);
    setCounts({});
  }, [selectedBranch]);

  useEffect(() => {
    setReportData(null);
    setCounts({});
  }, [businessDate, selectedCounter]);

  const { mutate: previewEod, isPending: isPreviewing } = useEodPreview();
  const { mutate: finalizeEod, isPending: isFinalizing } = useFinalizeEod();
  
  const { refetch: fetchFinalReport, isFetching: isFetchingFinal } = useGetFinalEodReport({
    branchId: Number(selectedBranch),
    counterId: selectedCounter ? Number(selectedCounter) : null,
    businessDate,
    enabled: false,
  });

  const handlePreview = () => {
    if (!selectedBranch || !businessDate) return;
    
    // Construct counts payload
    const countsPayload: EodCounterCountRequest[] = Object.keys(counts).map((id) => {
      const counterId = Number(id);
      const c = counts[counterId];
      return {
        counterId,
        countedCash: c.countedCash === "" ? 0 : c.countedCash,
        verifiedBank: c.verifiedBank === "" ? 0 : c.verifiedBank,
      };
    });

    previewEod(
      {
        branchId: Number(selectedBranch),
        counterId: selectedCounter ? Number(selectedCounter) : null,
        businessDate,
        counts: countsPayload.length > 0 ? countsPayload : undefined,
      },
      {
        onSuccess: (data) => {
          setReportData(data);
          // Only map server counts into local state if they exist, so we don't overwrite user's transient input with nulls incorrectly if the user had partial input.
          // Wait, if preview fails, onError keeps counts. If success, we update.
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err.message || "Preview failed";
          const reqId = err?.response?.data?.meta?.requestId;
          toast.error(`${msg} ${reqId ? `(Req ID: ${reqId})` : ""}`);
        }
      }
    );
  };

  const handleLoadFinal = () => {
    if (!selectedBranch || !businessDate) return;
    
    fetchFinalReport().then((res) => {
      if (res.isError) {
        const err: any = res.error;
        if (err?.response?.status === 404) {
          toast.info("No final snapshot found for this date.");
          setReportData(null);
        } else {
          const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err.message || "Failed to load final report";
          toast.error(msg);
        }
      } else if (res.data) {
        setReportData(res.data);
      }
    });
  };

  const handleFinalize = () => {
    setIsConfirmFinalizeOpen(false);
    if (!selectedBranch || !businessDate) return;

    finalizeEod(
      {
        branchId: Number(selectedBranch),
        counterId: selectedCounter ? Number(selectedCounter) : null,
        businessDate,
      },
      {
        onSuccess: (data) => {
          toast.success("EOD Finalized successfully");
          setReportData(data);
          setCounts({});
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err.message || "Finalization failed";
          const reqId = err?.response?.data?.meta?.requestId;
          toast.error(`${msg} ${reqId ? `(Req ID: ${reqId})` : ""}`);
        }
      }
    );
  };

  const handleDownloadPreviewPdf = async () => {
    try {
      const countsPayload: EodCounterCountRequest[] = Object.keys(counts).map((id) => {
        const counterId = Number(id);
        const c = counts[counterId];
        return {
          counterId,
          countedCash: c.countedCash === "" ? 0 : c.countedCash,
          verifiedBank: c.verifiedBank === "" ? 0 : c.verifiedBank,
        };
      });

      const blob = await downloadEodPreviewPdf({
        branchId: Number(selectedBranch),
        counterId: selectedCounter ? Number(selectedCounter) : null,
        businessDate,
        counts: countsPayload.length > 0 ? countsPayload : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eod-preview-${businessDate.replace(/-/g, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      toast.error("Failed to download Preview PDF");
    }
  };

  const handleDownloadPreviewExcel = async () => {
    try {
      const countsPayload: EodCounterCountRequest[] = Object.keys(counts).map((id) => {
        const counterId = Number(id);
        const c = counts[counterId];
        return {
          counterId,
          countedCash: c.countedCash === "" ? 0 : c.countedCash,
          verifiedBank: c.verifiedBank === "" ? 0 : c.verifiedBank,
        };
      });

      const blob = await downloadEodPreviewExcel({
        branchId: Number(selectedBranch),
        counterId: selectedCounter ? Number(selectedCounter) : null,
        businessDate,
        counts: countsPayload.length > 0 ? countsPayload : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eod-preview-${businessDate.replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      toast.error("Failed to download Preview Excel");
    }
  };

  const handleDownloadFinalPdf = async () => {
    try {
      const blob = await downloadFinalEodPdf({
        branchId: Number(selectedBranch),
        counterId: selectedCounter ? Number(selectedCounter) : null,
        businessDate,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eod-final-${businessDate.replace(/-/g, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      toast.error("Failed to download Final PDF");
    }
  };

  const handleDownloadFinalExcel = async () => {
    try {
      const blob = await downloadFinalEodExcel({
        branchId: Number(selectedBranch),
        counterId: selectedCounter ? Number(selectedCounter) : null,
        businessDate,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eod-final-${businessDate.replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      toast.error("Failed to download Final Excel");
    }
  };

  const handleCountChange = (counterId: number, field: "countedCash" | "verifiedBank", value: string) => {
    const num = value === "" ? "" : parseFloat(value);
    setCounts((prev) => ({
      ...prev,
      [counterId]: {
        ...prev[counterId],
        [field]: num,
      }
    }));
  };

  const inputCls = "h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]";
  const labelCls = "text-sm font-medium text-[#52627C] mb-1.5 block";
  
  const isFinal = reportData?.status === "FINAL";

  return (
    <div className="space-y-6">
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Branch <span className="text-red-500">*</span></label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className={cn(inputCls, "min-w-[220px] pr-8")}
              >
                <option value="">Select branch</option>
                {branchData?.content.map((b) => (
                  <option key={b.branchId} value={b.branchId}>{b.branchName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Business Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                max={today()}
                value={businessDate}
                onChange={(e) => setBusinessDate(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Counter (Optional)</label>
              <select
                value={selectedCounter}
                onChange={(e) => setSelectedCounter(e.target.value)}
                disabled={!selectedBranch}
                className={cn(inputCls, "min-w-[180px] pr-8 disabled:opacity-50")}
              >
                <option value="">All Counters</option>
                {counterData?.content.map((c) => (
                  <option key={c.id} value={c.id}>{c.counterName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePreview}
              disabled={!selectedBranch || !businessDate || isPreviewing || isFinalizing}
              className="h-11 rounded-[12px] bg-[#F0F4FF] px-5 text-sm font-medium text-[#1A5EF9] transition-colors hover:bg-[#E0E8FF] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPreviewing ? "Previewing..." : "Preview"}
            </button>
            <button
              onClick={handleDownloadPreviewPdf}
              disabled={!reportData || isFinal || isPreviewing}
              className="h-11 rounded-[12px] bg-white border border-[#E8EEF9] px-4 text-sm font-medium text-[#52627C] transition-colors hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <Download size={16} /> Preview PDF
            </button>
            <button
              onClick={handleDownloadPreviewExcel}
              disabled={!reportData || isFinal || isPreviewing}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-4 text-sm font-medium text-white transition-colors hover:bg-[#0E4FDE] disabled:opacity-50 flex items-center gap-2"
            >
              <FileSpreadsheet size={16} /> Preview Excel
            </button>
            <div className="w-px h-8 bg-[#E8EEF9] mx-1"></div>
            <button
              onClick={handleLoadFinal}
              disabled={!selectedBranch || !businessDate || isFetchingFinal}
              className="h-11 rounded-[12px] bg-white border border-[#E8EEF9] px-5 text-sm font-medium text-[#52627C] transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              {isFetchingFinal ? "Loading..." : "Load Final"}
            </button>
            <button
              onClick={() => setIsConfirmFinalizeOpen(true)}
              disabled={!reportData || isFinal || isFinalizing}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-sm font-medium text-white transition-colors hover:bg-[#0E4FDE] disabled:opacity-50 flex items-center gap-2"
            >
              <Save size={16} /> Finalize
            </button>
            <button
              onClick={handleDownloadFinalPdf}
              disabled={!reportData || !isFinal}
              className="h-11 rounded-[12px] bg-white border border-[#E8EEF9] px-4 text-sm font-medium text-[#52627C] transition-colors hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <Download size={16} /> Final PDF
            </button>
            <button
              onClick={handleDownloadFinalExcel}
              disabled={!reportData || !isFinal}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-4 text-sm font-medium text-white transition-colors hover:bg-[#0E4FDE] disabled:opacity-50 flex items-center gap-2"
            >
              <FileSpreadsheet size={16} /> Final Excel
            </button>
          </div>
        </div>
      </div>

      {!reportData ? (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white py-16 text-center shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <Landmark className="mx-auto mb-3 h-10 w-10 text-[#D0DAEE]" />
          <p className="text-sm font-medium text-[#52627C]">
            Select a branch and date, then load a preview or final report.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Report State Banner */}
          <div className={cn("p-4 rounded-[16px] flex items-center gap-3", 
            isFinal ? "bg-[#ECFDF5] text-[#0E9F6E]" : "bg-[#FFF8EB] text-[#F59E0B]"
          )}>
            {isFinal ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            <div>
              <h4 className="font-semibold">{isFinal ? "FINAL REPORT" : "PREVIEW - NOT FINALIZED"}</h4>
              <p className="text-sm opacity-90">Generated at: {new Date(reportData.generatedAt).toLocaleString()}</p>
            </div>
          </div>
          
          {reportData.reconciliation?.calculationMismatch !== 0 && (
             <div className="p-4 rounded-[16px] bg-[#FEF2F2] text-[#D14343] flex items-center gap-3">
               <AlertTriangle className="w-5 h-5" />
               <div>
                 <h4 className="font-semibold">Data Integrity Warning</h4>
                 <p className="text-sm">Calculation mismatch detected: {fmt(reportData.reconciliation.calculationMismatch, reportData.currency)}</p>
               </div>
             </div>
          )}

          {/* Summary Metrics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SummaryCard label="Projected EOD Funds" value={fmt(reportData.projectedEodFunds, reportData.currency)} tone="blue" icon={<Wallet className="h-5 w-5" />} />
            <SummaryCard label="Net Current Day" value={fmt(reportData.netCurrentDay, reportData.currency)} tone="green" icon={<Activity className="h-5 w-5" />} />
            <SummaryCard label="Accounts Receivable" value={fmt(reportData.accountsReceivable, reportData.currency)} tone="slate" icon={<ArrowDownLeft className="h-5 w-5" />} />
            <SummaryCard label="Accounts Payable" value={fmt(reportData.accountsPayable, reportData.currency)} tone="red" icon={<ArrowUpRight className="h-5 w-5" />} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Previous Closing */}
            <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-sm">
              <h3 className="text-base font-semibold text-[#21314A] mb-4">Previous Closing</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm border-b pb-2">
                  <span className="text-[#52627C]">Cash</span>
                  <span className="font-medium">{fmt(reportData.previousClosing.cash, reportData.currency)}</span>
                </div>
                <div className="flex justify-between items-center text-sm border-b pb-2">
                  <span className="text-[#52627C]">Bank</span>
                  <span className="font-medium">{fmt(reportData.previousClosing.bank, reportData.currency)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-[#21314A]">Total</span>
                  <span className="font-bold text-[#1A5EF9]">{fmt(reportData.previousClosing.total, reportData.currency)}</span>
                </div>
                <div className="text-xs text-[#8A9BB5] mt-2">
                  Source: {reportData.previousClosing.source} {reportData.previousClosing.sourceDate ? `(${reportData.previousClosing.sourceDate})` : ''}
                </div>
              </div>
            </div>

            {/* Receipts */}
            <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-sm">
              <h3 className="text-base font-semibold text-[#21314A] mb-4">Current-Day Receipts</h3>
              <div className="space-y-2">
                <ReceiptRow label="Cash" value={reportData.currentDayReceipts.cash} curr={reportData.currency} />
                <ReceiptRow label="POS" value={reportData.currentDayReceipts.pos} curr={reportData.currency} />
                <ReceiptRow label="Bank Transfer" value={reportData.currentDayReceipts.bankTransfer} curr={reportData.currency} />
                <ReceiptRow label="Cheque" value={reportData.currentDayReceipts.cheque} curr={reportData.currency} />
                <ReceiptRow label="Cash Excess" value={reportData.currentDayReceipts.cashExcess} curr={reportData.currency} isSecondary />
                <ReceiptRow label="Bank Excess" value={reportData.currentDayReceipts.bankExcess} curr={reportData.currency} isSecondary />
                <div className="flex justify-between items-center text-sm pt-2 border-t mt-2">
                  <span className="font-semibold text-[#21314A]">Total</span>
                  <span className="font-bold text-[#0E9F6E]">{fmt(reportData.currentDayReceipts.total, reportData.currency)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Outflows */}
            <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-sm">
              <h3 className="text-base font-semibold text-[#21314A] mb-4">Current-Day Outflows</h3>
              <div className="space-y-2">
                <ReceiptRow label="POS Charges" value={reportData.currentDayOutflows.posCharges} curr={reportData.currency} />
                <ReceiptRow label="Vendor Payments" value={reportData.currentDayOutflows.vendorPayments} curr={reportData.currency} />
                <ReceiptRow label="Salary" value={reportData.currentDayOutflows.salary} curr={reportData.currency} />
                <ReceiptRow label="Rent" value={reportData.currentDayOutflows.rent} curr={reportData.currency} />
                <ReceiptRow label="Bill Payments" value={reportData.currentDayOutflows.billPayments} curr={reportData.currency} />
                <ReceiptRow label="Sales Returns" value={reportData.currentDayOutflows.salesReturns} curr={reportData.currency} />
                <ReceiptRow label="Write-Offs" value={reportData.currentDayOutflows.writeOffs} curr={reportData.currency} />
                <ReceiptRow label="Commission" value={reportData.currentDayOutflows.commission} curr={reportData.currency} />
                <ReceiptRow label="Other" value={reportData.currentDayOutflows.other} curr={reportData.currency} />
                <ReceiptRow label="Cash Shortage" value={reportData.currentDayOutflows.cashShortage} curr={reportData.currency} isSecondary />
                <ReceiptRow label="Bank Shortage" value={reportData.currentDayOutflows.bankShortage} curr={reportData.currency} isSecondary />
                <div className="flex justify-between items-center text-sm pt-2 border-t mt-2">
                  <span className="font-semibold text-[#21314A]">Total</span>
                  <span className="font-bold text-[#D14343]">{fmt(reportData.currentDayOutflows.total, reportData.currency)}</span>
                </div>
              </div>
            </div>

            {/* Unsettled POS */}
            <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-sm">
              <h3 className="text-base font-semibold text-[#21314A] mb-4">Previous Unsettled POS</h3>
              {reportData.previousUnsettledPos.items.length === 0 ? (
                <div className="text-center py-6 text-sm text-[#8A9BB5]">
                  No unsettled POS items.
                </div>
              ) : (
                <div className="space-y-4">
                  {reportData.previousUnsettledPos.items.map((pos) => (
                    <div key={pos.sessionId} className="flex justify-between items-center text-sm border-b pb-2 last:border-0">
                      <div>
                        <div className="text-[#21314A] font-medium">{pos.counterName}</div>
                        <div className="text-[#8A9BB5] text-xs">{pos.businessDate} {pos.settledOnReportDate && "(Settled today)"}</div>
                      </div>
                      <span className="font-medium">{fmt(pos.amount, reportData.currency)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center text-sm pt-2 border-t">
                    <span className="font-semibold text-[#21314A]">Total</span>
                    <span className="font-bold">{fmt(reportData.previousUnsettledPos.total, reportData.currency)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Counter Reconciliation Table */}
          <div className="rounded-[20px] border border-[#E8EEF9] bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-[#E8EEF9]">
              <h3 className="text-lg font-semibold text-[#21314A]">Counter Reconciliation</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-[#F8FAFC] text-[#52627C]">
                  <tr>
                    <th className="px-6 py-4 font-medium">Counter</th>
                    <th className="px-6 py-4 font-medium text-right">Expected Cash</th>
                    <th className="px-6 py-4 font-medium text-right">Counted Cash</th>
                    <th className="px-6 py-4 font-medium text-right">Cash Variance</th>
                    <th className="px-6 py-4 font-medium text-right">Expected Bank</th>
                    <th className="px-6 py-4 font-medium text-right">Verified Bank</th>
                    <th className="px-6 py-4 font-medium text-right">Bank Variance</th>
                    <th className="px-6 py-4 font-medium text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8EEF9]">
                  {reportData.reconciliation.counters.map((c) => (
                    <tr key={c.counterId} className="hover:bg-[#F8FAFC] transition-colors">
                      <td className="px-6 py-4 font-medium text-[#21314A]">{c.counterName}</td>
                      <td className="px-6 py-4 text-right">{fmt(c.expectedCash, reportData.currency)}</td>
                      <td className="px-6 py-4 text-right">
                        {isFinal ? (
                          fmt(c.countedCash, reportData.currency)
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={counts[c.counterId]?.countedCash ?? c.countedCash ?? ""}
                            onChange={(e) => handleCountChange(c.counterId, "countedCash", e.target.value)}
                            className="w-28 h-9 rounded-lg border border-[#E6ECF5] px-3 text-right outline-none focus:border-[#BFD1FF]"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <VarianceBadge value={c.cashVariance} currency={reportData.currency} />
                      </td>
                      <td className="px-6 py-4 text-right">{fmt(c.expectedBank, reportData.currency)}</td>
                      <td className="px-6 py-4 text-right">
                        {isFinal ? (
                          fmt(c.verifiedBank, reportData.currency)
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={counts[c.counterId]?.verifiedBank ?? c.verifiedBank ?? ""}
                            onChange={(e) => handleCountChange(c.counterId, "verifiedBank", e.target.value)}
                            className="w-28 h-9 rounded-lg border border-[#E6ECF5] px-3 text-right outline-none focus:border-[#BFD1FF]"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <VarianceBadge value={c.bankVariance} currency={reportData.currency} />
                      </td>
                      <td className="px-6 py-4 text-center">
                        {c.balanced === null ? (
                          <span className="text-gray-400 text-xs">Not counted</span>
                        ) : c.balanced ? (
                          <span className="text-green-600 bg-green-50 px-2 py-1 rounded text-xs font-medium">Balanced</span>
                        ) : (
                          <span className="text-red-600 bg-red-50 px-2 py-1 rounded text-xs font-medium">Variance</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ConfirmPopup
        open={isConfirmFinalizeOpen}
        setOpen={setIsConfirmFinalizeOpen}
        onConfirm={handleFinalize}
        title="Finalize EOD Report"
        message={`Are you sure you want to finalize the EOD for Branch #${selectedBranch} on ${businessDate.split("-").reverse().join("-")}? This action is immutable and will generate the final report snapshot.`}
        confirmText={isFinalizing ? "Finalizing..." : "Finalize"}
        confirmColor="bg-blue-600"
      />
    </div>
  );
}

const ReceiptRow = ({ label, value, curr, isSecondary = false }: { label: string; value: number | null; curr: string | null; isSecondary?: boolean }) => (
  <div className="flex justify-between items-center text-sm py-1">
    <span className={cn(isSecondary ? "text-[#8A9BB5] italic" : "text-[#52627C]")}>{label}</span>
    <span className="font-medium">{fmt(value, curr)}</span>
  </div>
);

const VarianceBadge = ({ value, currency }: { value: number | null, currency: string | null }) => {
  if (value === null) return <span className="text-gray-400">-</span>;
  if (value === 0) return <span>{fmt(0, currency)}</span>;
  if (value > 0) return <span className="text-green-600">+{fmt(value, currency)}</span>;
  return <span className="text-red-600">{fmt(value, currency)}</span>;
};

type SummaryCardProps = {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: "green" | "red" | "blue" | "slate";
};

function SummaryCard({ label, value, icon, tone }: SummaryCardProps) {
  const toneClass = {
    green: "bg-[#ECFDF5] text-[#0E9F6E]",
    red: "bg-[#FEF2F2] text-[#D14343]",
    blue: "bg-[#F0F4FF] text-[#1A5EF9]",
    slate: "bg-[#F5F8FD] text-[#52627C]",
  }[tone];

  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-4 shadow-[0_8px_24px_rgba(26,94,249,0.06)]">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-[#52627C] truncate">{label}</p>
        <div className={cn("rounded-full p-2 flex-shrink-0", toneClass)}>{icon}</div>
      </div>
      <p className="mt-3 text-xl font-bold text-[#21314A]">{value}</p>
    </div>
  );
}

export default EodBalance;
